/**
 * AES-256-GCM item + DEK-wrap AEAD via WebCrypto `SubtleCrypto`.
 *
 * Wire format: `version(1) ‖ kind(1) ‖ iv(12) ‖ ciphertext ‖ tag(16)`.
 * WebCrypto appends the 16-byte tag to the ciphertext, so we store them
 * together. Two AAD constructions per spec §7:
 *   - ItemPayload: version ‖ 'item-v1' ‖ userId ‖ vaultId ‖ itemId ‖ contentVersion(BE32)
 *   - DekWrap:     version ‖ userId ‖ itemId ‖ 'dek-wrap'
 */
import { VERSION_V1, KindByte, assertHeader } from './version.js';
import { ItemAuthTagMismatchError } from './errors.js';
import { toArrayBuffer } from './bytes.js';
const IV_BYTES = 12;
const TAG_BYTES = 16;
const HEADER_BYTES = 2;
const enc = new TextEncoder();
const ITEM_DST = enc.encode('item-v1'); // 7-byte domain-separation tag
const DEKWRAP_DST = enc.encode('dek-wrap'); // 8-byte domain-separation tag
function concat(...parts) {
    const total = parts.reduce((n, p) => n + p.length, 0);
    const out = new Uint8Array(total);
    let off = 0;
    for (const p of parts) {
        out.set(p, off);
        off += p.length;
    }
    return out;
}
function be32(n) {
    if (!Number.isInteger(n) || n < 0 || n > 0xffffffff) {
        // Precondition, not a crypto failure: a non-uint32 contentVersion would be
        // silently truncated by `>>> 0`, letting two logical versions collide in the
        // AAD. Reject at the boundary. Message carries no plaintext/key material.
        throw new RangeError('contentVersion must be a uint32 (0..2^32-1)');
    }
    const b = new Uint8Array(4);
    new DataView(b.buffer).setUint32(0, n, false);
    return b;
}
const vbyte = Uint8Array.of(VERSION_V1);
/** Item AAD: version ‖ 'item-v1' ‖ userId ‖ vaultId ‖ itemId ‖ contentVersion. */
export function itemAad(args) {
    return concat(vbyte, ITEM_DST, args.userId, args.vaultId, args.itemId, be32(args.contentVersion));
}
/** DEK-wrap AAD: version ‖ userId ‖ itemId ‖ 'dek-wrap'. */
export function dekWrapAad(args) {
    return concat(vbyte, args.userId, args.itemId, DEKWRAP_DST);
}
/** Low-level AES-GCM seal. Returns `version ‖ kind ‖ iv ‖ ct‖tag`. */
export async function aesGcmSeal(key, kind, plaintext, aad) {
    const iv = crypto.getRandomValues(new Uint8Array(IV_BYTES));
    const ctAndTag = new Uint8Array(await crypto.subtle.encrypt({ name: 'AES-GCM', iv: toArrayBuffer(iv), additionalData: toArrayBuffer(aad) }, key, toArrayBuffer(plaintext)));
    const blob = new Uint8Array(HEADER_BYTES + IV_BYTES + ctAndTag.length);
    blob[0] = VERSION_V1;
    blob[1] = kind;
    blob.set(iv, HEADER_BYTES);
    blob.set(ctAndTag, HEADER_BYTES + IV_BYTES);
    return blob;
}
/**
 * Low-level AES-GCM open. Rejects unknown version/kind before reading ciphertext.
 * Throws the supplied `onAuthFail` error on tag failure (caller distinguishes
 * item vs DEK-unwrap).
 */
export async function aesGcmOpen(key, kind, blob, aad, onAuthFail) {
    const offset = assertHeader(blob, kind);
    if (blob.length < offset + IV_BYTES + TAG_BYTES) {
        throw onAuthFail();
    }
    const iv = blob.subarray(offset, offset + IV_BYTES);
    const ctAndTag = blob.subarray(offset + IV_BYTES);
    try {
        const pt = await crypto.subtle.decrypt({ name: 'AES-GCM', iv: toArrayBuffer(iv), additionalData: toArrayBuffer(aad) }, key, toArrayBuffer(ctAndTag));
        return new Uint8Array(pt);
    }
    catch {
        throw onAuthFail();
    }
}
/** Encrypt an item payload under its DEK. Spec §7. */
export async function encryptItem(args) {
    const aad = itemAad(args);
    return aesGcmSeal(args.dek, KindByte.ItemPayload, args.plaintext, aad);
}
/** Decrypt an item payload. Tag failure → {@link ItemAuthTagMismatchError}. */
export async function decryptItem(args) {
    const aad = itemAad(args);
    return aesGcmOpen(args.dek, KindByte.ItemPayload, args.blob, aad, () => new ItemAuthTagMismatchError());
}
//# sourceMappingURL=aead.js.map